import React from "react";
import { Button } from "react-bootstrap";
import { AiOutlineExpandAlt } from "react-icons/ai";
import { Link } from "react-router-dom";
import { S3Media } from "../../../third-party-services";
import SampleImage from "../../../../assets/logo/roiquant - image not found.png";

// Empty cell component
const EmptyTableCell = () => <p style={{ textAlign: "center" }}>-</p>;

// Expandable content container showing truncated text with expand button
const ExpandableContent = ({
  value,
  expandData,
  onClick,
  highlightText,
  highlight,
}) => (
  <div className="expandable">
    {typeof value === "string" ? (
      <p
        className="detailcutdata"
        dangerouslySetInnerHTML={{
          __html: highlightText(value, highlight),
        }}
      ></p>
    ) : (
      <div className="detailcutdata">{value}</div>
    )}
    <Button className="expand-button" onClick={() => onClick(expandData)}>
      <AiOutlineExpandAlt />
    </Button>
  </div>
);

// Table cell component
const TableCell = ({
  field,
  value,
  highlight,
  renderContent,
  highlightText,
  getSortAppliedBool,
  getFilterAppliedBool,
  hiddenFieldNames,
}) => {
  if (hiddenFieldNames.includes(field.actualName)) return null;

  return (
    <td
      sortapplied={getSortAppliedBool(field.actualName)}
      filterapplied={getFilterAppliedBool(field.actualName)}
      className={field.cName}
    >
      {!value ? (
        <EmptyTableCell />
      ) : renderContent ? (
        renderContent(value)
      ) : (
        <p
          dangerouslySetInnerHTML={{
            __html: highlightText(value, highlight),
          }}
        ></p>
      )}
    </td>
  );
};

// Table row component
const TableRow = ({ props2 }) => {
  const {
    state,
    argument,
    appliedFilterFieldNames,
    sortConditionsArray,
    tableData,
    fields,
    hiddenFieldNames,
    handleData,
    openExpandCard,
    getCompanyDetails,
    searchApplied,
  } = props2;

  // Highlight text function
  const highlightText = (text, searchTerm) => {
    // Return original input if not a string or if search isn't applied
    if (typeof text !== "string" || !searchApplied) return text;

    // Return original text if search term is empty
    if (!searchTerm || !searchTerm.trim()) return text;

    // Create highlight regex
    const words = searchTerm.trim().split(/\s+/).filter(Boolean);
    const regex = new RegExp(`(${words.join("|")})`, "gi");

    return text.replace(regex, `<span class="highlight">$1</span>`);
  };

  const getFilterAppliedBool = (fieldName) => {
    // console.log("appliedFilterFieldNames", appliedFilterFieldNames);

    // Check if the filter is applied and if any of the applied filter field names start with the given fieldName
    const isFilterApplied =
      argument.filter.applied &&
      appliedFilterFieldNames.some(
        (appliedField) =>
          appliedField.startsWith(`${fieldName}.`) || appliedField === fieldName
      );

    return isFilterApplied ? "true" : "false";
  };

  // Check if a sort is applied
  const getSortAppliedBool = (fieldName) =>
    sortConditionsArray.some((e) => e.actualName === fieldName)
      ? "true"
      : "false";

  // Props for table cells
  const tableCellProps = {
    hiddenFieldNames,
    getFilterAppliedBool,
    getSortAppliedBool,
    openExpandCard,
    highlightText,
    highlight: state.searchTermApplied,
  };

  // Field configuration
  const fieldConfig = {
    name: (investor) => ({
      value: investor.name,
      renderContent: (value) => (
        <Link
          to={`/investor/${investor.slug}`}
          target="_blank"
          rel="noopener noreferrer"
          className="shutdowndatalink"
          dangerouslySetInnerHTML={{
            __html: highlightText(value, state.searchTermApplied),
          }}
        />
      ),
    }),
    investorType: (investor) => ({
      value: investor.investorType,
      renderContent: (value) => (
        <ExpandableContent
          {...tableCellProps}
          value={value.join(", ")}
          expandData={value.join(", ")}
          onClick={openExpandCard}
        />
      ),
    }),
    description: (investor) => ({
      value: investor.description,
      renderContent: (value) => (
        <ExpandableContent
          {...tableCellProps}
          value={value}
          expandData={value}
          onClick={openExpandCard}
        />
      ),
    }),
    numOfFailedInvestments: (investor) => ({
      value: investor.numOfFailedInvestments,
    }),
    lossOnInvestments: (investor) => ({
      value: investor.lossOnInvestmentsObj?.str,
    }),
    shutteredPortfolioCompanies: (investor) => ({
      value: investor.shutteredPortfolioCompanies,
      // renderContent: (value) => (
      //   <ExpandableContent
      //     {...tableCellProps}
      //     value={value.join(", ")}
      //     expandData={{
      //       slug: investor.slug,
      //       field: "shutteredPortfolioCompanies",
      //       table: "investors",
      //     }}
      //     onClick={getCompanyDetails}
      //   />
      // ),
    }),
    numOfExit: (investor) => ({
      value: investor.numOfExit,
    }),
    exitValue: () => ({
      value: null, // Placeholder for empty cell
    }),
    reputation: () => ({
      value: null, // Placeholder for empty cell
    }),
    location: (investor) => ({
      value: investor.location,
      renderContent: (value) => (
        <p>
          {value.city && <span>{value.city}, </span>}
          {value.state && <span>{value.state}, </span>}
          {value.country && <span>{value.country}</span>}
        </p>
      ),
    }),
    investmentStage: (investor) => ({
      value: investor.investmentStage,
      renderContent: (value) => (
        <ExpandableContent
          {...tableCellProps}
          value={value.join(", ")}
          expandData={value.join(", ")}
          onClick={openExpandCard}
        />
      ),
    }),
    investmentTrackRecord: (investor) => ({
      value: investor.investmentTrackRecord,
      renderContent: (value) => (
        <div className="detaillongdata detailcutdata">
          <ol className="detailproductlist">
            {value.veryEarlyStage && (
              <li className="detailbodydata">
                Very-early stage: {value.veryEarlyStage}
              </li>
            )}
            {value.earlyStage && (
              <li className="detailbodydata">
                Early stage: {value.earlyStage}
              </li>
            )}
            {value.growthStage && (
              <li className="detailbodydata">
                Growth stage: {value.growthStage}
              </li>
            )}
            {value.lateStage && (
              <li className="detailbodydata">Late stage: {value.lateStage}</li>
            )}
            {value.postIPO && (
              <li className="detailbodydata">Post-IPO: {value.postIPO}</li>
            )}
          </ol>
        </div>
      ),
    }),
    assets: () => ({
      value: null, // Placeholder for empty cell
    }),
    investmentRange: (investor) => ({
      value: investor.investmentRange,
      renderContent: (value) => (
        <div className="detaillongdata detailcutdata">
          <ol className="detailproductlist">
            {value.veryEarlyStage && (
              <li className="detailbodydata">
                Very-early stage: {value.veryEarlyStage}
              </li>
            )}
            {value.earlyStage && (
              <li className="detailbodydata">
                Early stage: {value.earlyStage}
              </li>
            )}
            {value.growthStage && (
              <li className="detailbodydata">
                Growth stage: {value.growthStage}
              </li>
            )}
            {value.lateStage && (
              <li className="detailbodydata">Late stage: {value.lateStage}</li>
            )}
            {value.postIPO && (
              <li className="detailbodydata">Post-IPO: {value.postIPO}</li>
            )}
          </ol>
        </div>
      ),
    }),
    sweetSpot: (investor) => ({
      value: investor.sweetSpot,
      renderContent: (value) => (
        <div className="detaillongdata detailcutdata">
          <ol className="detailproductlist">
            {value.veryEarlyStage && (
              <li className="detailbodydata">
                Very-early stage: {value.veryEarlyStage}
              </li>
            )}
            {value.earlyStage && (
              <li className="detailbodydata">
                Early stage: {value.earlyStage}
              </li>
            )}
            {value.growthStage && (
              <li className="detailbodydata">
                Growth stage: {value.growthStage}
              </li>
            )}
            {value.lateStage && (
              <li className="detailbodydata">Late stage: {value.lateStage}</li>
            )}
            {value.postIPO && (
              <li className="detailbodydata">Post-IPO: {value.postIPO}</li>
            )}
          </ol>
        </div>
      ),
    }),
    investmentFocus: (investor) => ({
      value: investor.investmentFocus,
      renderContent: (value) => (
        <ExpandableContent
          {...tableCellProps}
          value={value.join(", ")}
          expandData={value.join(", ")}
          onClick={openExpandCard}
        />
      ),
    }),
    investmentGeography: (investor) => ({
      value: investor.investmentGeography,
      renderContent: (value) => (
        <ExpandableContent
          {...tableCellProps}
          value={value.join(", ")}
          expandData={value.join(", ")}
          onClick={openExpandCard}
        />
      ),
    }),
    numOfInvestments: (investor) => ({
      value: investor.numOfInvestments,
    }),
    numOfLeadInvestments: (investor) => ({
      value: investor.numOfLeadInvestments,
    }),
    numOfFollowOnInvestments: (investor) => ({
      value: investor.numOfFollowOnInvestments,
    }),
    numOfCoInvestments: (investor) => ({
      value: investor.numOfCoInvestments,
    }),
    portfolioCompanies: (investor) => ({
      value: investor.portfolioCompanies,
      renderContent: (value) => (
        <ExpandableContent
          {...tableCellProps}
          value={
            <ol className="detailproductlist detailcutdata">
              {value.map((company, index) =>
                company ? (
                  <li key={index} className="detailbodydata">
                    {company.name}
                  </li>
                ) : null
              )}
            </ol>
          }
          expandData={{
            slug: investor.slug,
            field: "portfolioCompanies",
            table: "investors",
          }}
          onClick={getCompanyDetails}
        />
      ),
    }),
    numOfAcquisitions: (investor) => ({
      value: investor.numOfAcquisitions,
    }),
    boardSeats: () => ({
      value: null, // Placeholder for empty cell
    }),
    numOfFunds: (investor) => ({
      value: investor.numOfFunds,
    }),
    limitedPartners: () => ({
      value: null, // Placeholder for empty cell
    }),
    coInvestors: (investor) => ({
      value: investor.coInvestors,
      renderContent: (value) => (
        <ExpandableContent
          {...tableCellProps}
          value={value.join(", ")}
          expandData={value.join(", ")}
          onClick={openExpandCard}
        />
      ),
    }),
    numofPartners: () => ({
      value: null, // Placeholder for empty cell
    }),
    partners: () => ({
      value: null, // Placeholder for empty cell
    }),
    numOfEmployeesObj: (investor) => ({
      value: investor.numOfEmployeesObj?.range,
    }),
    management: (investor) => ({
      value: investor.management,
      renderContent: (value) => (
        <ExpandableContent
          {...tableCellProps}
          value={
            <ol className="detailcutdata detailproductlist">
              {value.map((founder, index) =>
                founder ? (
                  <li key={index} className="detailbodydata">
                    {founder.name}
                  </li>
                ) : null
              )}
            </ol>
          }
          expandData={value}
          onClick={openExpandCard}
        />
      ),
    }),
    boardMembers: (investor) => ({
      value: investor.boardMembers,
      renderContent: (value) => (
        <ExpandableContent
          {...tableCellProps}
          value={
            <ol className="detailproductlist">
              {value.map((member, index) =>
                member ? (
                  <li key={index} className="detailbodydata">
                    {member.name}
                  </li>
                ) : null
              )}
            </ol>
          }
          expandData={value}
          onClick={openExpandCard}
        />
      ),
    }),
    founders: (investor) => ({
      value: investor.founders,
      renderContent: (value) => (
        <ExpandableContent
          {...tableCellProps}
          value={value.join(", ")}
          expandData={value.join(", ")}
          onClick={openExpandCard}
        />
      ),
    }),
    email: (investor) => ({
      value: investor.contact?.emails,
      renderContent: (value) => (
        <a className="detaillongdata shutdowndatalink" href={`mailto:${value}`}>
          {value}
        </a>
      ),
    }),
    socialMedia: (investor) => ({
      value: investor.socialMedia,
      renderContent: (value) => (
        <div className="detailmediumdata detailcutdata">
          <ul className="detailproductlist">
            {value.map((social, index) =>
              social ? (
                <li key={index} className="detailbodydata">
                  <a className="shutdowndatalink" href={social}>
                    {social.includes("twitter") ? "Twitter" : ""}
                    {social.includes("facebook") ? "Facebook" : ""}
                    {social.includes("linkedin") ? "LinkedIn" : ""}
                  </a>
                </li>
              ) : null
            )}
          </ul>
        </div>
      ),
    }),
    website: (investor) => ({
      value: investor.profileLink,
      renderContent: (value) => (
        <>
          {value.map((link, index) =>
            link.platform === "Website" ? (
              <a
                className="detaillongdata shutdowndatalink"
                href={link.url}
                key={index}
              >
                {link.url}
              </a>
            ) : null
          )}
        </>
      ),
    }),
    phone: (investor) => ({
      value: investor.contact?.phoneNumber,
    }),
    address: (investor) => ({
      value: investor.address,
      renderContent: (value) => (
        <p>
          {value.street1 && <span>{value.street1}, </span>}
          {value.street2 && <span>{value.street2}, </span>}
          {investor.location.city && <span>{investor.location.city}, </span>}
          {investor.location.country && (
            <span>{investor.location.country}</span>
          )}
        </p>
      ),
    }),
    entityType: (investor) => ({
      value: investor.entityType,
    }),
    legalName: (investor) => ({
      value: investor.legalName,
    }),
    primaryBusinessName: (investor) => ({
      value: investor.primaryBusinessName,
      renderContent: (value) => (
        <ExpandableContent
          {...tableCellProps}
          value={value.join(", ")}
          expandData={value.join(", ")}
          onClick={openExpandCard}
        />
      ),
    }),
    formerlegalName: (investor) => ({
      value: investor.former_legalName,
      renderContent: (value) => (
        <ExpandableContent
          {...tableCellProps}
          value={value.join(", ")}
          expandData={value.join(", ")}
          onClick={openExpandCard}
        />
      ),
    }),
    founded: (investor) => ({
      value: investor.founded?.dateStr,
    }),
    stockSymbol: (investor) => ({
      value: investor.stockSymbol,
    }),
  };

  return (
    <>
      {tableData.results.map((investor, index) => (
        <tr key={index}>
          <td>
            <p>
              {(argument.currentPage - 1) * argument.numOfRows + index + 1}.
            </p>
          </td>
          <td>
            <img
              src={
                investor.logo
                  ? `${S3Media}assets/photos/investors/${investor.logo}`
                  : SampleImage
              }
              alt={investor.name}
              className="shutteredlogo"
              onClick={() => handleData(investor.name)}
            />
          </td>
          {fields.map((field) => {
            const config = fieldConfig[field.actualName];
            if (!config) return null;

            const { value, renderContent } = config(investor);
            return (
              <TableCell
                key={field.actualName}
                {...tableCellProps}
                field={field}
                value={value}
                renderContent={renderContent}
              />
            );
          })}
        </tr>
      ))}
    </>
  );
};

export default TableRow;
